import React, { useState } from 'react';
import './PrivateEvents.css';
import eventsVideo from '../assets/drakes_events.MOV';
import eventsPoster from '../assets/host next event with us.jpg'; // Using an existing image as poster
import { submitFormspark } from '../utils/formspark';
import MarketingConsent from '../components/MarketingConsent';

const scrollToTop = () => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const PrivateEvents = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    company: '',
    date: '',
    time: '',
    guestCount: '',
    budget: '',
    moreDetails: '',
    hearAboutUs: '',
    marketingOptIn: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const formatTimeTo12Hour = (time) => {
    if (!time) return '';

    const [hourStr, minute] = time.split(':');
    if (hourStr === undefined || minute === undefined) {
      return time;
    }

    const parsedHour = parseInt(hourStr, 10);
    if (Number.isNaN(parsedHour)) {
      return time;
    }

    const period = parsedHour >= 12 ? 'PM' : 'AM';
    const hour12 = parsedHour % 12 || 12;

    return `${hour12}:${minute} ${period}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const formattedTime = formatTimeTo12Hour(formData.time);

    try {
      await submitFormspark({
        form: 'private-events',
        fullName: formData.fullName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        company: formData.company,
        eventDate: formData.date,
        eventTime: formattedTime,
        guestCount: formData.guestCount,
        budget: formData.budget,
        moreDetails: formData.moreDetails,
        hearAboutUs: formData.hearAboutUs,
        marketingOptIn: formData.marketingOptIn ? 'Yes' : 'No'
      });

      setSubmitStatus('success');
      setFormData({
        fullName: '',
        email: '',
        phoneNumber: '',
        company: '',
        date: '',
        time: '',
        guestCount: '',
        budget: '',
        moreDetails: '',
        hearAboutUs: '',
        marketingOptIn: false
      });
    } catch (error) {
      console.error('Private event request submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }

    scrollToTop();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="private-events-container">
      <div className="video-header">
        <video
          autoPlay
          loop
          muted
          playsInline
          poster={eventsPoster}
          className="events-video"
        >
          <source src={eventsVideo} type="video/mp4" />
          <source src={eventsVideo} type="video/quicktime" />
          Your browser does not support the video tag.
        </video>
      </div>

      <div className="virtual-tour-section">
        <h2 className="virtual-tour-title">Take a Virtual Tour</h2>
        <p className="virtual-tour-description">
          Experience Drake's in stunning 3D! Take a virtual walkthrough of our space to help plan your next event.
        </p>
        <a 
          href="https://www.rentvenuespace.com/drakes"
          target="_blank" 
          rel="noopener noreferrer" 
          className="virtual-tour-button"
        >
          View 3D Walkthrough
        </a>
      </div>
      
      <h1 className="private-events-title">For Private Events, fill out the form below!</h1>

      {submitStatus === 'success' && (
        <div className="success-message">
          Thank you! Your private event request has been sent. Our team will reach out soon.
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="error-message">
          Something went wrong while sending your request. Please try again or contact us directly.
        </div>
      )}

      <form onSubmit={handleSubmit} className="private-events-form">
        <div className="form-group">
          <label htmlFor="fullName">Full Name<span className="required">*</span></label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="Full Name"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email<span className="required">*</span></label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="form-group">
          <label htmlFor="phoneNumber">Phone Number<span className="required">*</span></label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            placeholder="(555) 555-5555"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="form-group">
          <label htmlFor="company">Company</label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Company Name"
            disabled={isSubmitting}
          />
        </div>

        <div className="form-group date-time-group">
          <label>Date and time of the event<span className="required">*</span></label>
          <div className="date-time-inputs">
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              disabled={isSubmitting}
            />
            <input
              type="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              required
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="guestCount">Guest Count<span className="required">*</span></label>
          <input
            type="number"
            id="guestCount"
            name="guestCount"
            value={formData.guestCount}
            onChange={handleChange}
            placeholder="Number of guests"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="form-group">
          <label htmlFor="budget">Budget<span className="required">*</span></label>
          <input
            type="number"
            id="budget"
            name="budget"
            value={formData.budget}
            onChange={handleChange}
            placeholder="Budget amount"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="form-group">
          <label htmlFor="moreDetails">More Details Here...</label>
          <textarea
            id="moreDetails"
            name="moreDetails"
            value={formData.moreDetails}
            onChange={handleChange}
            rows="4"
            disabled={isSubmitting}
          />
        </div>

        <div className="form-group">
          <label htmlFor="hearAboutUs">How did you hear about us?<span className="required">*</span></label>
          <input
            type="text"
            id="hearAboutUs"
            name="hearAboutUs"
            value={formData.hearAboutUs}
            onChange={handleChange}
            required
            disabled={isSubmitting}
          />
        </div>

        <MarketingConsent
          id="private-events-marketing-opt-in"
          checked={formData.marketingOptIn}
          onChange={handleChange}
          disabled={isSubmitting}
        />

        <button type="submit" className="submit-button" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Send event request'}
        </button>
      </form>
    </div>
  );
};

export default PrivateEvents; 